import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { extractSystemStyles } from '@famly/mf_system_system-props';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { Icon } from '@famly/mf_data-display_icon';
import { useResponsiveSize } from '@famly/mf_util_breakpoints';
export const TextField = ({ disabled, readOnly, placeholder, size: sizeFromProps, multiline, fullWidth, error, startAdornment, endAdornment, onChange, onFocus, value, type, minRows, maxRows, autoFocus, name, id, required, onBlur, onKeyDown, onKeyUp, autoComplete, inputProps, onPaste, ref, ...props }) => {
    const size = useResponsiveSize(sizeFromProps);
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);
    // The textbox role belongs to the <input>, so its accessible name and descriptions have to be set there.
    const _inputProps = { ...inputProps, ...ariaProps, 'data-e2e-class': 'text-field-input' };
    const _minRows = Math.max(minRows !== null && minRows !== void 0 ? minRows : 0, 3);
    const _maxRows = maxRows ? Math.max(3, maxRows) : undefined;
    const { sx: systemSx } = extractSystemStyles(props);
    // Password visibility state
    const [showPassword, setShowPassword] = React.useState(false);
    // Handle password visibility toggle
    const handleClickShowPassword = React.useCallback(() => {
        setShowPassword(show => !show);
    }, []);
    // Determine the actual input type
    const actualType = React.useMemo(() => {
        if (type === 'password' && showPassword) {
            return 'text';
        }
        return type;
    }, [type, showPassword]);
    // Create password toggle adornment
    const passwordToggleAdornment = React.useMemo(() => {
        if (type === 'password' && !multiline) {
            return (_jsx(InputAdornment, { position: "end", sx: { paddingRight: 2 }, children: _jsx(IconButton, { "aria-label": showPassword ? 'Hide password' : 'Show password', onClick: handleClickShowPassword, edge: "end", disabled: disabled, "data-e2e-class": "password-toggle-button", "data-cy-ignore": true, children: _jsx(Icon, { name: showPassword ? 'visibility_off' : 'visibility', size: size === 'regular' ? 24 : 20 }) }) }));
        }
        return null;
    }, [type, multiline, showPassword, handleClickShowPassword, disabled, size]);
    // Combine end adornments - password toggle takes precedence over custom endAdornment
    const combinedEndAdornment = React.useMemo(() => {
        if (multiline) {
            return undefined;
        }
        // If password toggle exists, use it instead of custom endAdornment
        if (passwordToggleAdornment) {
            return passwordToggleAdornment;
        }
        return endAdornment;
    }, [multiline, passwordToggleAdornment, endAdornment]);
    return (_jsx(OutlinedInput, { inputProps: _inputProps, placeholder: placeholder, size: size, disabled: disabled, readOnly: readOnly, multiline: multiline, minRows: _minRows, maxRows: _maxRows, fullWidth: fullWidth, error: error, startAdornment: multiline ? undefined : startAdornment, endAdornment: combinedEndAdornment, onChange: onChange, onFocus: onFocus, value: value, inputRef: ref, type: actualType, autoFocus: autoFocus, name: name, id: id, required: required, onBlur: onBlur, onKeyDown: onKeyDown, onKeyUp: onKeyUp, autoComplete: autoComplete, sx: systemSx, onPaste: onPaste, ...dataProps }));
};
